/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CrmDataEnrichmentHero.module.scss';

type Translate = (key: string) => string;

const FIELDS = [
  {
    label: 'Company name',
    before: 'northgate logistics',
    after: 'Northgate Logistics Limited',
    source: 'Registered name · Companies House',
  },
  {
    label: 'Registration no.',
    before: null,
    after: '12345678',
    source: 'England & Wales · verified 26 Jul 2026',
  },
  {
    label: 'Status',
    before: null,
    after: 'Active',
    source: 'Register check · 26 Jul 2026',
  },
  {
    label: 'Registered address',
    before: 'Unit 4, Northgate',
    after: '12 Kingsway, Rochdale, OL16 4NG',
    source: 'Address changed 12 Mar 2026',
  },
  {
    label: 'VAT number',
    before: null,
    after: 'GB 000 000 000',
    source: 'Tax authority register',
  },
  {
    label: 'Turnover',
    before: null,
    after: '£18.4m',
    source: 'Filed statutory accounts · FY2025',
  },
  {
    label: 'Ultimate parent',
    before: null,
    after: 'Northgate Holdings Limited',
    source: 'Beneficial ownership filing · 04 Feb 2026',
  },
];

const getStats = (t: Translate) => [
  { value: '400+', label: t('registries') },
  { value: '200+', label: t('countries') },
  { value: t('Daily'), label: t('sync') },
];

const getChecklist = (t: Translate) => [
  t('Duplicate accounts merged automatically'),
  t('Missing firmographics written back to your CRM'),
  t('Every field cites the register and filing date'),
];

const REGISTRIES = [
  { flag: '🇬🇧', name: 'Companies House', country: 'GB' },
  { flag: '🇩🇪', name: 'Handelsregister', country: 'DE' },
  { flag: '🇮🇹', name: 'Registro Imprese', country: 'IT' },
  { flag: '🇫🇷', name: 'Insee Sirene', country: 'FR' },
  { flag: '🇳🇱', name: 'KVK Handelsregister', country: 'NL' },
  { flag: '🇪🇸', name: 'Registro Mercantil', country: 'ES' },
  { flag: '🇺🇸', name: 'SEC EDGAR', country: 'US' },
  { flag: '🇨🇦', name: 'Corporations Canada', country: 'CA' },
  { flag: '🇦🇺', name: 'ASIC', country: 'AU' },
  { flag: '🇸🇪', name: 'Bolagsverket', country: 'SE' },
  { flag: '🇳🇴', name: 'Brønnøysundregistrene', country: 'NO' },
  { flag: '🇩🇰', name: 'CVR Erhvervsstyrelsen', country: 'DK' },
  { flag: '🇫🇮', name: 'Kaupparekisteri PRH', country: 'FI' },
  { flag: '🇨🇭', name: 'Zefix', country: 'CH' },
  { flag: '🇵🇱', name: 'Krajowy Rejestr Sądowy', country: 'PL' },
  { flag: '🇮🇪', name: 'Companies Registration Office', country: 'IE' },
  { flag: '🇸🇬', name: 'ACRA', country: 'SG' },
  { flag: '🇭🇰', name: 'Companies Registry', country: 'HK' },
  { flag: '🇧🇷', name: 'Receita Federal CNPJ', country: 'BR' },
  { flag: '🇿🇦', name: 'CIPC', country: 'ZA' },
];

export const CrmDataEnrichmentHero = () => {
  const { t } = useInt();

  const instRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [revealed, setRevealed] = useState(0);

  const stats = getStats(t);
  const checklist = getChecklist(t);

  const matched = revealed >= FIELDS.length;

  useEffect(() => {
    const node = instRef.current;

    if (!node) {
      return undefined;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      setRevealed(FIELDS.length);
      return undefined;
    }

    let started = false;

    const step = (i: number) => {
      setRevealed(i + 1);

      if (i + 1 < FIELDS.length) {
        timeoutRef.current = setTimeout(() => step(i + 1), 420);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            timeoutRef.current = setTimeout(() => step(0), 500);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.25 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Section theme="dark" className={styles.root}>
      <div className={styles.grid}>
        <TitleBlock
          size="page"
          align="left"
          theme="dark"
          className={styles.titleBlock}
          title={t('Every account in your CRM, resolved to a registered legal entity.')}
        />
        <div className={styles.copyRight}>
          <div className={styles.panel}>
            <div className={styles.stats}>
              {stats.map((stat) => (
                <div key={stat.label} className={styles.stat}>
                  <b>{stat.value}</b>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>

            <ul className={styles.checklist}>
              {checklist.map((item) => (
                <li key={item}>
                  <span className={styles.check}>✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className={styles.lede}>
            {t(
              'Global Database reads your HubSpot, Salesforce or Dynamics 365 database, matches each record against government registries and writes back what was missing.',
            )}
          </p>

          <div className={styles.actions}>
            <Button type="white" size="xxl" href="/free-trial">
              {t('Start free trial')}
            </Button>

            <Button type="white-outline" size="xxl" href="/book-a-demo">
              {t('Run it on our data')}
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.inst} ref={instRef}>
        <div className={styles.instBar}>
          <span className={styles.crumb}>
            CRM <span className={styles.sep}>›</span> Company record <span className={styles.sep}>›</span> <b>sample</b>
          </span>
          <span className={cn(styles.state, matched && styles.stateOn)}>
            <span className={styles.stateDot} />
            {matched ? 'Matched · Companies House 12345678' : t('Matching against registries…')}
          </span>
        </div>

        <div className={styles.instHead}>
          <span>{t('Field')}</span>
          <span>{t('As it sits in the CRM')}</span>
          <span className={styles.headAfter}>{t('After matching')}</span>
        </div>

        {FIELDS.map((field, i) => {
          const isRevealed = i < revealed;

          return (
            <div key={field.label} className={cn(styles.row, isRevealed && styles.rowOn)}>
              <span className={styles.key}>{field.label}</span>
              <span className={styles.before}>{field.before ? <s>{field.before}</s> : t('Empty')}</span>
              <span className={styles.after}>
                <span className={styles.afterValue}>
                  <span className={styles.checkDot}>✓</span>
                  {field.after}
                </span>
                <span className={styles.afterSource}>{field.source}</span>
              </span>
            </div>
          );
        })}
      </div>

      <div className={styles.marquee} aria-label="Government registers we source from">
        <div className={styles.marqueeTrack}>
          {[...REGISTRIES, ...REGISTRIES].map((registry, i) => (
            <span key={`${registry.name}-${i}`} className={styles.marqueeItem}>
              <span className={styles.marqueeFlag}>{registry.flag}</span>
              <b>{registry.name}</b>
              <i>{registry.country}</i>
            </span>
          ))}
        </div>
      </div>
    </Section>
  );
};
